// web/src/pages/admin/JobStatusCodesPage.tsx
//
// J-9 — Admin CRUD for job status codes (`/api/job-status-codes`).
//
// Features:
//   - List view: code · short desc · color swatch · jobs-using count ·
//     sort order · is-terminal · stuck-after-days · Edit / Delete.
//   - "New Code" button opens a drawer with a create form.
//   - Edit click opens the same drawer pre-populated.
//   - Delete is disabled (tooltip "In use by N jobs") when jobsUsingCount > 0.
//   - When jobsUsingCount === 0: click → confirm → remove → invalidate.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  jobsApi,
  type JobStatusCodeDto,
  type CreateJobStatusCodeRequest,
} from "@/lib/jobs";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailModal } from "@/components/detail-modal";
import { EntityForm } from "@/components/entity-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { RowActions } from "@/components/row-actions";

// ---------------------------------------------------------------------------
// Form schema
// ---------------------------------------------------------------------------

const schema = z.object({
  statusCode: z.string().min(1).max(40),
  shortDesc: z.string().min(1).max(100),
  longDesc: z.string().nullable(),
  sortOrder: z.number().int(),
  color: z.string().nullable(),
  isTerminal: z.boolean(),
  stuckAfterDays: z.number().int().nullable(),
  statusGroupCode: z.string().nullable(),
  defaultNextStateCode: z.string().nullable(),
});
type Form = z.infer<typeof schema>;

function emptyForm(): Form {
  return {
    statusCode: "",
    shortDesc: "",
    longDesc: null,
    sortOrder: 0,
    color: null,
    isTerminal: false,
    stuckAfterDays: null,
    statusGroupCode: null,
    defaultNextStateCode: null,
  };
}

function rowToForm(r: JobStatusCodeDto): Form {
  return {
    statusCode: r.statusCode,
    shortDesc: r.shortDesc,
    longDesc: r.longDesc,
    sortOrder: r.sortOrder,
    color: r.color,
    isTerminal: r.isTerminal,
    stuckAfterDays: r.stuckAfterDays,
    statusGroupCode: r.statusGroupCode,
    defaultNextStateCode: r.defaultNextStateCode,
  };
}

// ---------------------------------------------------------------------------
// Color swatch helper
// ---------------------------------------------------------------------------

function ColorSwatch({ color }: { color: string | null }) {
  if (!color) return <span className="text-muted-foreground text-xs">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden
        className="inline-block h-4 w-4 rounded-sm border"
        style={{ backgroundColor: color }}
      />
      <span className="text-xs font-mono">{color}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function JobStatusCodesPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<JobStatusCodeDto | null>(null);
  const [creating, setCreating] = useState(false);

  const list = useQuery({
    queryKey: ["job-status-codes"],
    queryFn: () => jobsApi.statusCodes.list(),
  });

  const upsert = useMutation({
    mutationFn: async (form: Form) => {
      if (editing) {
        const body: import("@/lib/jobs").UpdateJobStatusCodeRequest = {
          shortDesc: form.shortDesc,
          longDesc: form.longDesc,
          sortOrder: form.sortOrder,
          color: form.color,
          isTerminal: form.isTerminal,
          stuckAfterDays: form.stuckAfterDays,
          statusGroupCode: form.statusGroupCode,
          defaultNextStateCode: form.defaultNextStateCode,
        };
        await jobsApi.statusCodes.update(editing.statusCode, body);
      } else {
        const body: CreateJobStatusCodeRequest = {
          statusCode: form.statusCode,
          shortDesc: form.shortDesc,
          longDesc: form.longDesc,
          sortOrder: form.sortOrder,
          color: form.color,
          isTerminal: form.isTerminal,
          stuckAfterDays: form.stuckAfterDays,
          statusGroupCode: form.statusGroupCode,
          defaultNextStateCode: form.defaultNextStateCode,
        };
        await jobsApi.statusCodes.create(body);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["job-status-codes"] });
      setEditing(null);
      setCreating(false);
    },
  });

  const del = useMutation({
    mutationFn: (code: string) => jobsApi.statusCodes.remove(code),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["job-status-codes"] }),
  });

  const cols: DataGridColumn<JobStatusCodeDto>[] = [
    { id: "code",      header: "Code",        accessor: (r) => r.statusCode },
    { id: "shortDesc", header: "Short desc",  accessor: (r) => r.shortDesc },
    {
      id: "color",
      header: "Color",
      accessor: (r) => <ColorSwatch color={r.color} />,
    },
    {
      id: "jobsUsing",
      header: "Jobs using",
      accessor: (r) => r.jobsUsingCount,
      align: "right",
    },
    {
      id: "sortOrder",
      header: "Sort order",
      accessor: (r) => r.sortOrder,
      align: "right",
    },
    {
      id: "isTerminal",
      header: "Terminal?",
      accessor: (r) => (r.isTerminal ? "Yes" : "—"),
    },
    {
      id: "stuckAfter",
      header: "Stuck after (days)",
      accessor: (r) => (r.stuckAfterDays != null ? String(r.stuckAfterDays) : "—"),
      align: "right",
    },
    {
      id: "actions",
      header: "",
      accessor: (r) => {
        const inUse = r.jobsUsingCount > 0;
        return (
          <RowActions
            label={r.statusCode}
            onEdit={() => setEditing(r)}
            onDelete={() => del.mutate(r.statusCode)}
            deleteDisabled={inUse || del.isPending}
            deleteTooltip={inUse ? `In use by ${r.jobsUsingCount} job${r.jobsUsingCount !== 1 ? "s" : ""}` : undefined}
          />
        );
      },
      align: "right",
    },
  ];

  const drawerOpen = creating || editing !== null;
  const defaultValues: Form = editing ? rowToForm(editing) : emptyForm();

  return (
    <ListPage backTo="/admin"
      title="Job status codes"
      primaryAction={
        <IntentButton onClick={() => setCreating(true)}>
          <Plus className="mr-2 h-4 w-4" /> Add Status Code
        </IntentButton>
      }
    >
      <DataGrid
        rows={list.data ?? []}
        columns={cols}
        rowKey={(r) => r.statusCode}
        totalRows={list.data?.length ?? 0}
        pageIndex={0}
        pageSize={Math.max(50, list.data?.length ?? 0)}
        loading={list.isLoading}
        onPageChange={() => undefined}
      />

      <DetailModal
        open={drawerOpen}
        onOpenChange={(o) => {
          if (!o) {
            setEditing(null);
            setCreating(false);
          }
        }}
        title={editing ? "Edit job status code" : "New job status code"}
      >
        <EntityForm<Form>
          schema={schema}
          defaultValues={defaultValues}
          onSubmit={(v) => upsert.mutate(v)}
        >
          {(form) => (
            <div className="space-y-3">
              <div>
                <Label htmlFor="jsc-code">Status code</Label>
                <Input
                  id="jsc-code"
                  disabled={editing !== null}
                  {...form.register("statusCode")}
                />
              </div>
              <div>
                <Label htmlFor="jsc-short">Short description</Label>
                <Input id="jsc-short" {...form.register("shortDesc")} />
              </div>
              <div>
                <Label htmlFor="jsc-long">Long description (optional)</Label>
                <Input
                  id="jsc-long"
                  {...form.register("longDesc", {
                    setValueAs: (v: string) => v === "" ? null : v,
                  })}
                />
              </div>
              <div>
                <Label htmlFor="jsc-color">Color (hex, e.g. #3b82f6)</Label>
                <Input
                  id="jsc-color"
                  {...form.register("color", {
                    setValueAs: (v: string) => v === "" ? null : v,
                  })}
                />
              </div>
              <div>
                <Label htmlFor="jsc-sort">Sort order</Label>
                <Input
                  id="jsc-sort"
                  type="number"
                  {...form.register("sortOrder", {
                    setValueAs: (v: string) => v === "" ? 0 : Number(v),
                  })}
                />
              </div>
              <div>
                <Label htmlFor="jsc-stuck">Stuck after days (optional)</Label>
                <Input
                  id="jsc-stuck"
                  type="number"
                  {...form.register("stuckAfterDays", {
                    setValueAs: (v: string) => v === "" ? null : Number(v),
                  })}
                />
              </div>
              <div>
                <Label htmlFor="jsc-group">Status group code (optional)</Label>
                <Input
                  id="jsc-group"
                  {...form.register("statusGroupCode", {
                    setValueAs: (v: string) => v === "" ? null : v,
                  })}
                />
              </div>
              <div>
                <Label htmlFor="jsc-next">Default next state code (optional)</Label>
                <Input
                  id="jsc-next"
                  {...form.register("defaultNextStateCode", {
                    setValueAs: (v: string) => v === "" ? null : v,
                  })}
                />
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" {...form.register("isTerminal")} />
                Is terminal
              </label>
              <Button type="submit">Save</Button>
            </div>
          )}
        </EntityForm>
      </DetailModal>
    </ListPage>
  );
}
